import { FC, useContext, useMemo, useState, useTransition, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Card, Box, Grid, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Dashboard, Edit } from '@mui/icons-material';
import uniqueId from 'lodash/uniqueId';
import isEmpty from 'lodash/isEmpty';

import { RefHandle, TimelineForm } from '@modules/Tributes/components/TimelineForm';
import { useTribute } from '@modules/Tributes/hooks/useTribute';
import { CreateTributeCard } from '@modules/Tributes/components/CreateTributeCard';
import { MarketingButtons } from '@modules/Tributes/components/MarketingButtons';
import { useMe } from '@modules/Auth/hooks/useMe';
import { useFormDataTransform } from '@modules/Tributes/hooks/useFormDataTransform';
import { useTributeCrud } from '@modules/Tributes/hooks/useTributeCrud';
import { FeedbackContext } from '@cvt/contexts';

interface Props {
  tributeId?: number | string;
}

export const ViewTribute: FC<Props> = (props) => {
  const tributeFormRef = useRef<RefHandle>(null);
  const [isEditMode, setEditMode] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { tributeId } = props;

  const { user } = useMe();
  const { tribute } = useTribute({ id: tributeId! });
  const { editTribute } = useTributeCrud();
  const { toTributeCreateCrud } = useFormDataTransform();
  const { genericErrorFeedback } = useContext(FeedbackContext);

  const defaultFormValues: Tributes.FormData & { _key: string } = useMemo(() => {

    return {
      _key: uniqueId(),
      ...tribute,
      pet: {
        petType: tribute?.pet?.petType || '',
        sex: tribute?.pet?.sex || '',
        name: tribute?.pet?.name || '',
        birthday: tribute?.pet?.birthday ? new Date(tribute.pet.birthday) : null,
        passingDate: tribute?.pet?.passingDate ? new Date(tribute.pet.passingDate) : null,
        favouriteMemory: tribute?.pet?.favouriteMemory || '',
        personality: tribute?.pet?.personality || '',
        specialHabits: tribute?.pet?.specialHabits || '',
      },
      timeline: (tribute?.memories ?? []).map(event => {
        const memory = {
          ...event,
          media: event.media as Tributes.Media[] || [],
        };

        return {
          saved: { ...memory },
          draft: { ...memory },
        };
      }),
      mainPhoto: tribute?.mainPhoto as Tributes.Media || null,
    };
  }, [tribute]);

  const canCreateTributeCard = useMemo(() => {
    if (user?.id === tribute?.author && isEmpty(tribute?.parcel) && tribute?.status === 'published') {
      return true;
    }
  }, [user, tribute]);

  const handleSubmitRequest = (data: Tributes.FormData) => {
    if (!tribute) {
      return;
    }

    startTransition(async () => {
      try {
        const tributeData = await toTributeCreateCrud(data);
        await editTribute.mutateAsync({ id: tribute.id, ...tributeData });

        setEditMode(false);
      } catch {
        genericErrorFeedback();
      }
    });
  };

  const handleCancelClick = () => {
    tributeFormRef.current?.resetForm();
    setEditMode(false);
  };

  return (
    <>
      {tribute?.marketingUrl && (
        <Box sx={{ position: 'sticky', top: 0, zIndex: 9999, width: '100%', maxWidth:'sm', mx:'auto' }}>
          <MarketingButtons />
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Grid container maxWidth="sm" width="100%" height="100%" mx="auto">
          <Grid size={12}>
            <Stack maxWidth="sm" sx={{ py: 2, px: { xs: 1, sm: 0 } }}>
              {user && tribute && user.id === tribute.author && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    width: { xs: '100%', lg: 'auto' },
                    p: 2,
                    position: 'sticky',
                    top: { xs: '56px', sm: '64px' },
                    zIndex: 2,
                    backgroundColor: 'white',
                  }}
                >
                  <Button
                    component={Link}
                    to="/tributes/my-tributes"
                    variant="contained"
                    startIcon={<Dashboard sx={{ width: 16, height: 16 }} />}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      height: '44px',
                      bgcolor: 'rgb(242, 110, 99)',
                      '&:hover': {
                        bgcolor: 'rgb(220, 95, 85)',
                      },
                      color: 'white',
                      minHeight: '44px',
                      textTransform: 'none',
                      borderRadius: '12px',
                    }}
                  >
                    Visit Your Dashboard
                  </Button>

                  {isEditMode ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" color="secondary" onClick={handleCancelClick}>
                        Cancel
                      </Button>
                      <LoadingButton
                        variant="outlined"
                        type="submit"
                        form="tribute-form"
                        loading={isPending}
                      >
                        Save Changes
                      </LoadingButton>
                    </Stack>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<Edit sx={{ width: 16, height: 16 }} />}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        height: '44px',
                        borderColor: 'rgb(156, 39, 176)',
                        color: 'rgb(156, 39, 176)',
                        '&:hover': {
                          bgcolor: 'rgba(156, 39, 176, 0.05)',
                          borderColor: 'rgb(156, 39, 176)',
                        },
                        minHeight: '44px',
                        textTransform: 'none',
                        borderRadius: '12px',
                      }}
                      onClick={() => setEditMode(!isEditMode)}
                    >
                      Edit Your LGCY
                    </Button>
                  )}
                </Box>
              )}
              <Card sx={{
                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgb(254, 238, 237)',
                borderRadius: '12px',
                overflow: 'visible',
                pt: 1,
                pb: { xs: 1, sm: 2 },
              }}
              >
                <TimelineForm
                  ref={tributeFormRef}
                  readonly={!isEditMode}
                  isEditing={isEditMode}
                  key={defaultFormValues._key}
                  defaultValues={defaultFormValues}
                  tributeStatus={tribute?.status}
                  tributeUrl={`${import.meta.env.VITE__CVT_VITE__BASE_URL_FOR_URL_GENERATION}/tributes/view/${tributeId}`}
                  onSubmitRequest={handleSubmitRequest}
                />
              </Card>
              {(canCreateTributeCard && tribute) && (
                <Box mt={2} width="100%">
                  <CreateTributeCard
                    tribute={tribute}
                  />
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>


    </>);

};